import { FormikTextInput } from '@forms/Components/TextInput';
import { Field, useFormikContext } from 'formik';

interface EquipmentFieldProps {
	name: string;
	label: string;
	className?: string;
	disabled?: boolean;
	disabledReason?: string;
}

export default function EquipmentField({ name, label, className, disabled, disabledReason }: EquipmentFieldProps) {
	const { setFieldValue } = useFormikContext();

	return (
		<Field
			name={name}
			label={label}
			className={className}
			component={FormikTextInput}
			onBlurExtension={(event: React.FocusEvent<HTMLInputElement>) => {
				const formattedValue = formatEquipmentValue(event.target.value);
				void setFieldValue(name, formattedValue);
			}}
			disabled={disabled}
			disabledReason={disabled ? disabledReason : undefined}
		/>
	);
}

/**
 * Formats the equipment value to match the pattern "AAAA DDDDDD-D" or "AAAA DDDD".
 */
export function formatEquipmentValue(value: string) {
	// Match pattern: exactly 4 letters followed by digits
	const match = value
		.replaceAll('-', '')
		.replaceAll(' ', '')
		.trim()
		.match(/^([A-Za-z]{4})(\d+)$/);

	if (!match) {
		// Doesn't match the pattern (e.g., ABCD or ABCDE383838), return as-is
		return value.toUpperCase();
	}

	const letters = match[1].toUpperCase();
	const digits = match[2];

	if (digits.length >= 6 && digits.length <= 7) {
		// Format as AAAA DDDDDD-D (4 letters, space, 6 digits, dash, remaining)
		const firstPart = digits.slice(0, 6);
		const secondPart = digits.slice(6);
		const formatted = `${letters} ${firstPart}-${secondPart}`;
		// Trim trailing '-' if no digits follow
		return formatted.replace(/-$/, '');
	} else {
		// Format as AAAA DDDD (4 letters, space, digits)
		return `${letters} ${digits}`;
	}
}
